import { Capacitor } from "@capacitor/core";
import { CapacitorCalendar } from "@ebarooni/capacitor-calendar";
import {
  FilteredItineraryEntry,
  getUserOrgPref,
  getUserPref,
  ICategory,
  setUserOrgPref,
  spLogTags,
} from "@eventr/common";
import { isAndroid, isIOS } from "react-device-detect";
import { useDeviceInfo } from "../../feat/appState/deviceInfo";
import i18n from "../../i18n/i18next";
import { PermissionsPlugin } from "../../plugins/permissions/PermissionsPlugin";
import { bs } from "../../state/bound.store";
import { filteredEntriesForTarget } from "../../state/filterTargetsState/filterTargets.adapter";
import { FilterSelection } from "../../state/filterTargetsState/filterTargets.interface";
import { IItinerary } from "../../state/interfaces";
import { replaceRenderOptions } from "../../state/itineraryState/itineraryMap.slice";
import { compareBuildVersion } from "../../utils";
import { buildItemUniversalLink } from "@eventr/common";
import { reactAppStage } from "../../utils/reactAppStage";

/** Payload accepted by the plugin's `createEvent` call. */
type CalendarEventPayload = {
  calendarId: string;
  title: string;
  startDate: number;
  endDate: number;
  // Event alerts in minutes relative to the start. @ebarooni/capacitor-calendar reads negative
  // values as "before the start", so a 15-minute-early reminder is -15. Empty/undefined = no alert.
  alerts?: number[];
  location?: string;
  description?: string;
  // Deeplink back to the item. iOS surfaces this as a tappable event URL; Android ignores it (the
  // link is added to the description instead).
  url?: string;
};

/** A device calendar we created, recorded so we can update or remove it later. */
type TrackedCalendar = { itinId: number; fingerprint: string; calId: string };

const CAL_PREF_NAMESPACE = "homepage";
const CAL_PREF_KEY = "calendarSync";
// Events default to an hour when the entry has no usable end date.
const DEFAULT_EVENT_DURATION_MS = 60 * 60 * 1000;

/** ---------------------------------------------------------------------------
 * Reconciliation: device calendars are derived from `filterTargets[id].calendar`
 * and ItineraryFilter, mirroring how notifications/widgets are derived. One device
 * calendar is created per included itinerary; a fingerprint of (version + selection)
 * decides when an existing calendar is stale and must be rebuilt.
 * --------------------------------------------------------------------------- */

let reconciling = false;
let rerunQueued = false;
// Set once an "off" teardown has cleared every Eventr calendar, so repeated reconciles while sync
// stays off don't keep listing the device calendars. Reset whenever we build calendars again.
let calendarsCleared = false;
// `${itinId}:${fingerprint}` keys whose calendar creation just failed (e.g. no iCloud source on the
// device). Skipped on later reconciles so a persistent failure doesn't retry on every store change.
// Cleared on teardown, and naturally bypassed when an itinerary's fingerprint changes.
const failedBuilds = new Set<string>();

/**
 * Reconciles the device's Eventr calendars against the current store state: the global
 * `calendarSyncEnabled` toggle and each itinerary's `filterTargets.calendar` selection.
 * Safe to call often — it no-ops off-device, is single-flight, and only touches calendars
 * whose itinerary, selection, or version actually changed.
 */
export async function reconcileCalendars(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  if (reconciling) {
    // Coalesce overlapping triggers into a single follow-up run on the latest state.
    rerunQueued = true;
    return;
  }
  reconciling = true;
  try {
    await runReconcile();
  } catch (error) {
    spLogTags("Calendar reconcile failed", ["CALENDAR"], error);
  } finally {
    reconciling = false;
    if (rerunQueued) {
      rerunQueued = false;
      void reconcileCalendars();
    }
  }
}

async function runReconcile(): Promise<void> {
  const state = bs();
  const orgName = state.selectedOrg?.name;

  // Globally off, no permission, or logged out → remove every Eventr calendar from the device.
  // Runs regardless of the feature flag or selected org so turning sync off always cleans up, even
  // for calendars we no longer have tracked. The flag stops us re-listing while sync stays off.
  if (!state.calendarSyncEnabled || !state.calendarPermissionGranted || !state.wpUser) {
    if (!calendarsCleared) {
      spLogTags(
        `Calendar reconcile: tearing down (syncEnabled=${state.calendarSyncEnabled}, permission=${state.calendarPermissionGranted}, user=${Boolean(state.wpUser)})`,
        ["CALENDAR"],
      );
      await deleteAllEventrCalendars();
      if (orgName) writeTrackedCalendars(orgName, new Map());
      calendarsCleared = true;
      failedBuilds.clear();
    }
    return;
  }
  calendarsCleared = false;

  if (!orgName) {
    spLogTags("Calendar reconcile: skipped, no selected org", ["CALENDAR"]);
    return;
  }
  const buildVersion = useDeviceInfo.getState().version.buildVersion;
  if (!isCalendarEnabled(buildVersion)) {
    spLogTags(`Calendar reconcile: skipped, feature disabled for build version ${buildVersion}`, ["CALENDAR"]);
    return;
  }

  const tracked = readTrackedCalendars(orgName);
  const orgCategories = state.orgConfig?.categories ?? [];
  const defaultReminder = Number(getUserPref("account", "calendarReminders")) || 15;
  const reminderEnabled = Boolean(getUserPref("account", "calendarReminderEnabled"));
  const mapIds = new Set(state.itineraryMap.map((element) => element.id));

  // Tracking can drift from the real Calendar store, so cross-check against the live list and rebuild
  // any tracked calendar that is missing. If listing fails, trust the tracking pref.
  let existingCalIds: Set<string> | null = null;
  try {
    const { result } = await CapacitorCalendar.listCalendars();
    existingCalIds = new Set(result.map((calendar) => calendar.id));
  } catch (error) {
    spLogTags("Calendar reconcile: could not list device calendars", ["CALENDAR"], error);
  }

  // 1. Drop calendars that left the map, were switched off, vanished from the device, or are stale.
  //    Itineraries that simply aren't downloaded yet are left untouched.
  for (const [itinId, record] of [...tracked]) {
    const selection = state.getFilterSelection(itinId, "calendar");
    const itinerary = state.itineraryData[itinId];

    if (!mapIds.has(itinId) || !selection.enabled) {
      await deleteCalendarById(record.calId);
      tracked.delete(itinId);
      continue;
    }
    // Missing from the device, so drop it from tracking and let the build step recreate it.
    if (existingCalIds && !existingCalIds.has(record.calId)) {
      tracked.delete(itinId);
      continue;
    }
    if (
      itinerary &&
      calendarFingerprint(itinerary, selection, reminderEnabled, defaultReminder) !== record.fingerprint
    ) {
      await deleteCalendarById(record.calId);
      tracked.delete(itinId);
    }
  }

  // 2. Build a calendar for every downloaded, included itinerary that isn't already current.
  for (const element of state.itineraryMap) {
    const itinId = element.id;
    const itinerary = state.itineraryData[itinId];
    if (!itinerary || tracked.has(itinId)) continue;

    const selection = state.getFilterSelection(itinId, "calendar");
    if (!selection.enabled) continue;

    const fingerprint = calendarFingerprint(itinerary, selection, reminderEnabled, defaultReminder);
    // Don't re-attempt a build that already failed for this exact state (e.g. no iCloud source).
    if (failedBuilds.has(`${itinId}:${fingerprint}`)) continue;

    const title = element.itinerary_name?.trim() || "Itinerary";
    const calId = await createCalendarForItinerary(
      itinerary,
      selection,
      orgCategories,
      title,
      defaultReminder,
      reminderEnabled,
    );
    if (calId) {
      tracked.set(itinId, { itinId, fingerprint, calId });
    } else {
      failedBuilds.add(`${itinId}:${fingerprint}`);
    }
  }

  writeTrackedCalendars(orgName, tracked);
  spLogTags(`Calendar reconcile: ${tracked.size} calendar(s) synced for ${orgName}`, ["CALENDAR"]);
}

/**
 * Creates one device calendar for an itinerary and populates it with the entries that survive
 * the itinerary's calendar selection. Returns the created calendar id, or null on failure.
 */
async function createCalendarForItinerary(
  itinerary: IItinerary,
  selection: FilterSelection,
  orgCategories: ICategory[],
  title: string,
  defaultReminder: number,
  reminderEnabled: boolean,
): Promise<string | null> {
  try {
    // Only the current user's entries reach the device calendar — visibility is applied by
    // ItineraryFilter via the section user-lists (replaceRenderOptions ensures those are present).
    const entries = filteredEntriesForTarget(
      replaceRenderOptions(itinerary).v2,
      selection,
      "calendar",
      orgCategories,
      bs().getCurrentUser() ?? bs().wpUser,
    );

    let calId: string;

    if (Capacitor.getPlatform() === "android") {
      const { result: existingCalendars } = await CapacitorCalendar.listCalendars();
      const firstAccount = existingCalendars.find((cal) => cal.accountName)?.accountName;
      if (!firstAccount) {
        spLogTags("No calendar account found on device", ["CALENDAR"]);
        return null;
      }
      const androidCal = await CapacitorCalendar.createCalendar({
        title: `EventR - ${title}`,
        color: getRandomColor(),
        accountName: firstAccount,
        ownerAccount: firstAccount,
      });
      calId = androidCal.id;
    } else {
      const iosCal = await CapacitorCalendar.createCalendar({
        title: `EventR - ${title}`,
        color: getRandomColor(),
      });
      calId = iosCal.id;
    }
    // No sourceId → the plugin falls back to the device's default calendar source, so creation
    // works without iCloud (e.g. on a simulator) as well as with it.

    const events = entries
      .map((entry) => entryToEvent(entry, calId, defaultReminder, reminderEnabled))
      .filter((event): event is CalendarEventPayload => event !== null);

    await Promise.all(
      events.map((event) =>
        CapacitorCalendar.createEvent(event).catch((error: unknown) => {
          spLogTags(`Failed to create calendar event: ${event.title}`, ["CALENDAR"], error);
          return null;
        }),
      ),
    );

    spLogTags(`Created calendar "EventR - ${title}" with ${events.length} events`, ["CALENDAR"]);
    return calId;
  } catch (error) {
    spLogTags(`Failed to create calendar for itinerary: ${title}`, ["CALENDAR"], error);
    return null;
  }
}

/** Maps a filtered itinerary entry to a device-calendar event, or null if it has no start date. */
function entryToEvent(
  entry: FilteredItineraryEntry,
  calendarId: string,
  defaultReminder: number,
  reminderEnabled: boolean,
): CalendarEventPayload | null {
  if (entry.startDateMs === undefined) return null;

  const startDate = entry.startDateMs;
  const endDate =
    entry.endDateMs && entry.endDateMs > startDate ? entry.endDateMs : startDate + DEFAULT_EVENT_DURATION_MS;

  const data = entry.component as Record<string, unknown>;
  const entityId = typeof data._id === "string" ? data._id : undefined;
  // Universal link to the item. The `url` field is iOS-only, so on Android the link goes in the notes.
  const deeplink = entityId
    ? buildItemUniversalLink(entry.sectionId, entityId, reactAppStage(), bs().selectedOrg?.name)
    : undefined;
  const includeLinkInNotes = Capacitor.getPlatform() === "android" ? deeplink : undefined;

  return {
    calendarId,
    title: entry.name?.trim() || "Event",
    startDate,
    endDate,
    // Negative = before the start (plugin convention); defaultReminder is a positive lead-time.
    alerts: reminderEnabled ? [-Math.abs(defaultReminder)] : undefined,
    location: entryLocation(data),
    description: entryDescription(data, includeLinkInNotes),
    url: deeplink,
  };
}

/** Best-effort human-readable location from an entry's component (address lookups vary by section). */
function entryLocation(data: Record<string, unknown>): string | undefined {
  const osm = (data.OSMAddress ?? data.address) as { display_name?: unknown } | undefined;
  if (osm && typeof osm.display_name === "string" && osm.display_name.trim()) return osm.display_name.trim();
  for (const key of ["pick_up_location", "drop_off_location", "location"]) {
    const value = data[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return undefined;
}

/** Builds the event notes from an entry's freeform text, a booking reference line, and (Android) deeplink. */
function entryDescription(data: Record<string, unknown>, deeplink?: string): string | undefined {
  const parts: string[] = [];
  for (const key of ["notes", "additional_information", "description"]) {
    const value = data[key];
    if (typeof value === "string" && value.trim()) parts.push(value.trim());
  }

  // Booking reference, when the entry has one. Its field name varies by section.
  const bookingRef = [data.booking_ref, data.bookref, data.booking_reference, data.confirmationNumber].find(
    (value) => typeof value === "string" && value.trim(),
  );
  if (typeof bookingRef === "string") parts.push(`${i18n.t("cards.booking-reference")}: ${bookingRef.trim()}`);

  if (deeplink) parts.push(deeplink);
  return parts.length ? parts.join("\n\n") : undefined;
}

async function deleteCalendarById(calId: string): Promise<void> {
  try {
    await CapacitorCalendar.deleteCalendar({ id: calId });
  } catch (error) {
    spLogTags(`Failed to delete calendar: ${calId}`, ["CALENDAR"], error);
  }
}

/**
 * A calendar must be rebuilt when its itinerary version, its calendar selection, or its reminder
 * settings change. Including the reminder values means changing the lead-time (or the one-time fix to
 * the alert sign) re-fingerprints every tracked calendar, so existing device events get corrected.
 */
function calendarFingerprint(
  itinerary: IItinerary,
  selection: FilterSelection,
  reminderEnabled: boolean,
  defaultReminder: number,
): string {
  const sections = [...selection.excludedSections].sort((a, b) => a - b).join(",");
  const categories = [...selection.excludedCategories].sort().join(",");
  const reminder = reminderEnabled ? defaultReminder : "off";
  return `${itinerary.version ?? 0}:${sections}|${categories}|r${reminder}`;
}

// ---------------------------------------------------------------------------
// Tracking store: which device calendars we created, keyed by itinerary id. Persisted
// per-org via user prefs. Tolerant of the legacy "itinVersion: X itinId: Y - calId" format.
// ---------------------------------------------------------------------------

function readTrackedCalendars(orgName: string): Map<number, TrackedCalendar> {
  const raw = getUserOrgPref(CAL_PREF_NAMESPACE, CAL_PREF_KEY, orgName);
  const list: string[] = Array.isArray(raw) ? raw : raw ? [raw] : [];
  const tracked = new Map<number, TrackedCalendar>();
  for (const item of list) {
    const record = parseTrackedCalendar(item);
    if (record) tracked.set(record.itinId, record);
  }
  return tracked;
}

function parseTrackedCalendar(item: string): TrackedCalendar | null {
  try {
    const parsed = JSON.parse(item);
    if (parsed && typeof parsed.itinId === "number" && typeof parsed.calId === "string") {
      return { itinId: parsed.itinId, fingerprint: String(parsed.fingerprint ?? ""), calId: parsed.calId };
    }
  } catch {
    // Fall through to the legacy string format below.
  }
  // Legacy format: "itinVersion: 1 itinId: 123 - <deviceCalendarId>". Force a rebuild via a
  // fingerprint that can never match a freshly computed one, so the calendar is recreated cleanly.
  const match = item.match(/itinId:\s*(\d+).*-\s*(.+)$/);
  if (match && match[2]) return { itinId: Number(match[1]), fingerprint: "legacy", calId: match[2].trim() };
  return null;
}

function writeTrackedCalendars(orgName: string, tracked: Map<number, TrackedCalendar>): void {
  const list = [...tracked.values()].map((record) => JSON.stringify(record));
  setUserOrgPref(CAL_PREF_NAMESPACE, CAL_PREF_KEY, list, orgName);
}

/** Deletes every Eventr calendar on the device. Used when sync is switched off and on logout. */
export async function deleteAllEventrCalendars(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const { result: calendars } = await CapacitorCalendar.listCalendars();
    // Case-insensitive so it also clears calendars created under the older "Eventr -" name.
    const eventrCalendars = calendars.filter((calendar) => calendar.title?.toLowerCase().includes("eventr -"));
    await Promise.all(eventrCalendars.map((calendar) => deleteCalendarById(calendar.id)));
    spLogTags(`Deleted ${eventrCalendars.length} Eventr calendar(s)`, ["CALENDAR"]);
  } catch (error) {
    spLogTags("Failed to delete Eventr calendars", ["CALENDAR"], error);
  }
}

// capacitor-calendar 8.10 types `color` as a `#` hex string.
const getRandomColor = (): `#${string}` => {
  const letters = "0123456789ABCDEF";
  let color: `#${string}` = "#";
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
};

// ---------------------------------------------------------------------------
// Platform gating & permissions (unchanged behaviour).
// ---------------------------------------------------------------------------

const getIOSVersion = () => {
  if (navigator.userAgent.includes("iPhone") || navigator.userAgent.includes("iPad")) {
    const userAgent = navigator.userAgent;

    const match = userAgent.match(/OS (\d+)_?(\d+)?/);

    if (match) {
      const majorVersion = parseInt(match?.[1] ?? "0", 10);
      const minorVersion = match[2] ? parseInt(match[2], 10) : 0;
      return majorVersion + minorVersion / 10;
    }
  }
  return null;
};

const isBelowIOS17 = () => {
  const iosVersion = getIOSVersion();
  if (iosVersion !== null && iosVersion < 17) {
    spLogTags(`Phone is below iOS 17, calendar feature is disabled`, ["CALENDAR"]);
    return true;
  } else {
    spLogTags(`Phone is iOS 17 or above, calendar feature is enabled`, ["CALENDAR"]);
    return false;
  }
};

export const isCalendarEnabled = (version: string) => {
  const featureEnabled = compareBuildVersion(version, "1.4.0");
  if (Capacitor.isNativePlatform()) {
    if (isAndroid) {
      return featureEnabled;
    } else if (isIOS) {
      return featureEnabled && !isBelowIOS17();
    }
  } else return false;
};

/** The permission scopes this app actually uses. The plugin also reports the two reminders scopes. */
const CALENDAR_SCOPES = ["readCalendar", "writeCalendar"] as const;

/**
 * Pulls the calendar-scope permission states out of a `checkAllPermissions`/request response,
 * tolerant of both platforms' shapes. iOS returns `{ result: { readCalendar: "granted", … } }` with
 * clean string values; Android serialises the nested `result` from a raw `Map<enum, enum>` (so those
 * values are not the lowercase states the checks below expect) but does expose the correct flat scope
 * keys on the top-level object. Reading both and lower-casing lets one check work everywhere.
 *
 * Only the calendar scopes are read. The plugin always reports readReminders/writeReminders too, and
 * this app never requests reminders access, so they sit at "prompt" forever — counting them made a
 * permanently denied calendar look re-promptable. Falls back to every value if the response carries
 * neither scope key, so an unexpected shape can't read as "no permission at all".
 */
const permissionStateValues = (response: Record<string, unknown>): string[] => {
  const nested = (response.result ?? {}) as Record<string, unknown>;
  const values: string[] = [];
  for (const scope of CALENDAR_SCOPES) {
    for (const source of [response, nested]) {
      const value = source[scope];
      if (value !== undefined && value !== null) values.push(String(value).toLowerCase());
    }
  }
  if (values.length) return values;
  return Object.values({ ...response, ...nested }).map((value) => String(value).toLowerCase());
};

// Set while getCalendarSyncPerm has an OS prompt in flight. Presenting the iOS permission dialog
// resigns the app, so dismissing it fires an `appStateChange` → checkCalInitialPermissions that
// raced the still-pending request and could overwrite its result with a stale read — which showed
// up as the sync toggle flicking back off just after the user allowed access.
let permissionRequestInFlight = false;

/**
 * Reads the OS calendar permission without prompting, so the gated controls and the reconcile
 * reflect reality as soon as the app opens (and again on every foreground). The actual OS prompt
 * only ever fires from getCalendarSyncPerm, triggered by the sync toggle.
 */
export const checkCalInitialPermissions = async () => {
  if (!Capacitor.isNativePlatform()) return;
  // An in-flight request is the authoritative answer and is about to write the store itself; this
  // check would be reading the status mid-prompt.
  if (permissionRequestInFlight) {
    spLogTags("Calendar permission check skipped: a request is in flight", ["CALENDAR"]);
    return;
  }
  try {
    const response = await CapacitorCalendar.checkAllPermissions();
    const values = permissionStateValues(response);
    const hasPermission = values.includes("granted");
    const denied = !hasPermission && !values.some((value) => value === "prompt" || value === "prompt-with-rationale");
    bs().setCalendarPermissionGranted(hasPermission);
    bs().setCalendarPermissionDenied(denied);
    spLogTags(`Calendar permission granted: ${hasPermission}`, ["CALENDAR"]);
  } catch (error) {
    // Leave the last known state alone. A transient bridge failure is not evidence that the user
    // revoked access, and writing false here switched calendar sync off in the UI on foreground.
    spLogTags(`Error checking calendar permissions`, ["CALENDAR"], error);
  }
};

/** Outcome of an OS calendar-permission request. "prompt" means it can still be asked again. */
export type CalendarPermissionResult = "granted" | "denied" | "prompt" | "unavailable";

/**
 * Ensures the OS calendar permission, prompting when it hasn't been decided. Writes the result into
 * the store (which drives the reconcile) and resolves with the outcome so a denied caller can route
 * the user to the device settings. No-ops to "unavailable" off-native. Relocated here from the
 * now-removed home calendar-sync row so Settings and the per-itinerary controls share one routine.
 */
export async function getCalendarSyncPerm(): Promise<CalendarPermissionResult> {
  if (!Capacitor.isNativePlatform()) return "unavailable";
  permissionRequestInFlight = true;
  try {
    const outcome = await resolveCalendarPermission();
    bs().setCalendarPermissionGranted(outcome === "granted");
    bs().setCalendarPermissionDenied(outcome === "denied");
    return outcome;
  } finally {
    permissionRequestInFlight = false;
  }
}

/** Checks, and where still promptable requests, the OS calendar permission. Pure of store writes. */
async function resolveCalendarPermission(): Promise<CalendarPermissionResult> {
  try {
    const checked = await CapacitorCalendar.checkAllPermissions();
    if (permissionStateValues(checked).includes("granted")) return "granted";

    if (Capacitor.getPlatform() === "android") {
      const status = (await PermissionsPlugin.requestCalendarPermissions()) as unknown as Record<string, string>;
      if (status?.["calendar"] === "granted") return "granted";
    } else {
      // iOS. Deliberately NOT `requestAllPermissions()`: that is deprecated and, besides the calendar,
      // also asks EventKit for full *Reminders* access. This app ships no NSRemindersUsageDescription,
      // so EventKit fails that second request and the whole promise rejects — a user who had just
      // allowed calendar access was recorded as denied, and the sync toggle snapped straight back off.
      const { result } = await CapacitorCalendar.requestFullCalendarAccess();
      if (String(result).toLowerCase() === "granted") return "granted";
    }

    // A request reports a flat "denied" whether the user refused permanently, refused once (Android
    // can still re-prompt), or the OS never showed a dialog. The stored status is the real answer.
    return await readCalendarPermission();
  } catch (error) {
    // A rejected request says nothing about the stored authorization status, so read it rather than
    // assume the worst — otherwise one failed call routes the user to Settings for a permission the
    // OS has already granted.
    spLogTags("Could not request calendar permissions", ["CALENDAR"], error);
    return await readCalendarPermission();
  }
}

/** Classifies the OS calendar permission from its stored status, without prompting. */
async function readCalendarPermission(): Promise<CalendarPermissionResult> {
  try {
    const values = permissionStateValues(await CapacitorCalendar.checkAllPermissions());
    if (values.includes("granted")) return "granted";
    // Still promptable (e.g. the user dismissed without choosing) vs permanently denied.
    return values.some((value) => value === "prompt" || value === "prompt-with-rationale") ? "prompt" : "denied";
  } catch (error) {
    spLogTags("Could not read calendar permissions", ["CALENDAR"], error);
    return "denied";
  }
}
